<template>
  <div class="sidebar-nav">
    <transition name="sidebar-drill" mode="out-in">
      <ul
        v-if="!drillParent"
        key="main"
        class="app-sidebar__list list-unstyled mb-0"
      >
        <li class="app-sidebar__item">
          <router-link
            v-tooltip.right="sidebarTip('Dashboard')"
            to="/dashboard"
            class="app-sidebar__link app-sidebar__link--pill"
            @click="emitNavigated"
          >
            <i class="fa fa-home app-sidebar__icon" aria-hidden="true" />
            <span class="app-sidebar__label">Dashboard</span>
          </router-link>
        </li>
        <li class="app-sidebar__item">
          <router-link
            v-tooltip.right="sidebarTip('Calendar')"
            to="/calendar"
            class="app-sidebar__link app-sidebar__link--pill"
            @click="emitNavigated"
          >
            <i class="fa fa-calendar app-sidebar__icon" aria-hidden="true" />
            <span class="app-sidebar__label">Calendar</span>
          </router-link>
        </li>
        <li class="app-sidebar__item">
          <router-link
            v-tooltip.right="
              sidebarTip('My ' + $filters.customerLabel('plural', true))
            "
            :to="{ name: 'patients' }"
            class="app-sidebar__link app-sidebar__link--pill"
            :class="{
              'router-link-active': $route.path.startsWith('/clients'),
            }"
            @click="emitNavigated"
          >
            <i class="fa fa-users app-sidebar__icon" aria-hidden="true" />
            <span class="app-sidebar__label"
              >My {{ $filters.customerLabel("plural", true) }}</span
            >
          </router-link>
        </li>
        <li
          class="app-sidebar__item"
          v-if="!publicSettingsLoading && publicSettings.GROUP_SESSIONS_ENABLED"
        >
          <router-link
            v-tooltip.right="sidebarTip('Group Sessions')"
            to="/group-sessions"
            class="app-sidebar__link app-sidebar__link--pill"
            :class="{
              'router-link-active': $route.path.startsWith('/group-sessions'),
            }"
            @click="emitNavigated"
          >
            <i class="fa fa-users-cog app-sidebar__icon" aria-hidden="true" />
            <span class="app-sidebar__label">Group Sessions</span>
          </router-link>
        </li>
        <UnreadAlert :collapsed="collapsed" @navigated="emitNavigated" />
        <li class="app-sidebar__item">
          <router-link
            v-tooltip.right="sidebarTip('Meeting Rooms')"
            :to="{ name: 'rooms' }"
            class="app-sidebar__link app-sidebar__link--pill"
            :class="{ 'router-link-active': $route.path.startsWith('/rooms') }"
            @click="emitNavigated"
          >
            <i class="fa fa-door-open app-sidebar__icon" aria-hidden="true" />
            <span class="app-sidebar__label">Meeting Rooms</span>
          </router-link>
        </li>
        <PendingBookingRequests
          :collapsed="collapsed"
          @navigated="emitNavigated"
        />
        <li class="app-sidebar__item">
          <button
            v-tooltip.right="sidebarTip('My Profile')"
            type="button"
            class="
              app-sidebar__link
              app-sidebar__link--pill
              app-sidebar__link--parent
              w-100
              text-left
            "
            :class="{
              'app-sidebar__link--section-active':
                activeDrillSectionFromRoute === 'profile',
            }"
            @click="openDrill('profile')"
          >
            <i class="fa fa-user app-sidebar__icon" aria-hidden="true" />
            <span class="app-sidebar__label">My Profile</span>
            <i
              class="fa fa-chevron-right app-sidebar__chevron"
              aria-hidden="true"
            />
          </button>
        </li>
      </ul>
      <ul
        v-else-if="drillParent === 'profile'"
        key="profile"
        class="app-sidebar__list list-unstyled mb-0"
      >
        <li class="app-sidebar__item">
          <a
            href="/"
            class="app-sidebar__link app-sidebar__link--pill"
            @click.prevent="profileNavigate"
          >
            <span class="app-sidebar__label">Profile Update</span>
          </a>
        </li>
        <li class="app-sidebar__item">
          <a
            href="#"
            class="app-sidebar__link app-sidebar__link--pill"
            @click.prevent="handleLogout"
          >
            <span class="app-sidebar__label">Logout</span>
          </a>
        </li>
      </ul>
    </transition>
  </div>
</template>

<script>
import { mapActions, mapState } from "vuex";
import PendingBookingRequests from "@/components/PendingBookingRequests.vue";
import UnreadAlert from "@/components/UnreadAlert.vue";

export default {
  name: "SidebarNavProvider",
  components: {
    UnreadAlert,
    PendingBookingRequests,
  },
  props: {
    collapsed: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["drill-change", "navigated"],
  data() {
    return {
      drillParent: null,
      suppressRouteSync: false,
    };
  },
  computed: {
    ...mapState({
      user: (state) => state.auth.user,
      publicSettings: (state) => state.settings.publicSettings?.data ?? {},
      publicSettingsLoading: (state) =>
        state.settings.publicSettings?.loading ?? true,
      deviceType: (state) => state.settings.deviceType,
    }),
    activeDrillSectionFromRoute() {
      return this.computeDrillFromPath(this.$route.path);
    },
    isMobileTablet() {
      return this.deviceType === "mobile" || this.deviceType === "tablet";
    },
  },
  watch: {
    $route: {
      handler() {
        this.suppressRouteSync = false;
        this.applyRouteToDrill();
      },
      immediate: true,
    },
  },
  mounted() {
    this.getPublicSettings();
  },
  methods: {
    ...mapActions({
      getPublicSettings: "settings/getPublicSettings",
      doLogout: "auth/logout",
    }),
    sidebarTip(text) {
      if (!this.collapsed || this.isMobileTablet) return false;
      return text;
    },
    computeDrillFromPath(path) {
      const id = this.user?.provider_id;
      if (!id || !path) return null;
      if (path === `/providers/${id}` || path.startsWith(`/providers/${id}/`)) {
        return "profile";
      }
      return null;
    },
    applyRouteToDrill() {
      if (this.suppressRouteSync) return;
      if (this.collapsed && !this.isMobileTablet) {
        this.drillParent = null;
        this.emitDrillState(false);
        return;
      }
      this.drillParent = this.computeDrillFromPath(this.$route.path);
      this.emitDrillState(false);
    },
    openDrill(key) {
      this.suppressRouteSync = false;
      this.drillParent = key;
      this.emitDrillState(true);
    },
    goBackDrill() {
      this.suppressRouteSync = true;
      this.drillParent = null;
      this.emitDrillState(false);
    },
    emitDrillState(userInitiated = false) {
      if (!this.drillParent) {
        this.$emit("drill-change", {
          showBack: false,
          title: "",
          userInitiated: !!userInitiated,
        });
        return;
      }
      this.$emit("drill-change", {
        showBack: true,
        title: "My Profile options",
        userInitiated: !!userInitiated,
      });
    },
    emitNavigated() {
      this.$emit("navigated");
    },
    profileNavigate() {
      if (this.user) {
        this.$router.push({ path: `/providers/${this.user.provider_id}` });
      }
      this.emitNavigated();
    },
    handleLogout() {
      this.doLogout().then((res) => {
        if (res) {
          this.$router.push({ path: "/login" });
          window.location.href = `${window.location.origin}`;
        }
      });
    },
  },
};
</script>
